import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { DraftState } from '../hooks/useDraft';
import {
  QuickSaleAddressModel,
  QuickSaleUserModel,
} from '../models/QuickSaleModel';
import { FieldRow, QuickSaleCard } from './QuickSaleCard';

type Props = {
  user: QuickSaleUserModel;
  address: QuickSaleAddressModel | null;
  draftState: DraftState;
};

const ADDRESS_FIELDS = ['street', 'postcode', 'city'] as const;

export function formatAddress(address?: QuickSaleAddressModel | null) {
  if (!address) {
    return '-';
  }
  return [address.street, address.city, address.postcode]
    .filter(Boolean)
    .join(', ');
}

const CustomerCard: FC<Props> = ({ user, address, draftState }) => {
  const intl = useIntl();
  const [editing, setEditing] = useState(false);
  const { draft, update } = draftState;
  const draftAddress =
    draft.address && draft.address.id === address?.id ? draft.address : {};

  const email = draft.user?.email ?? user.email;
  const phone = draft.user?.phone ?? user.phone;

  function onUserChange(field: 'email' | 'phone', value: string) {
    update('user', { [field]: value === user[field] ? undefined : value });
  }

  function onAddressChange(
    field: (typeof ADDRESS_FIELDS)[number],
    value: string
  ) {
    if (!address) {
      return;
    }
    update('address', {
      id: address.id,
      [field]: value === address[field] ? undefined : value,
    });
  }

  return (
    <QuickSaleCard
      icon="fa-user"
      title={intl.formatMessage({ id: 'quickSale.customer' })}
      editing={editing}
      onToggleEdit={() => setEditing(!editing)}
    >
      <FieldRow label={intl.formatMessage({ id: 'quickSale.name' })}>
        {[user.name, user.last_name].filter(Boolean).join(' ') || '-'}
      </FieldRow>
      <FieldRow
        label={intl.formatMessage({ id: 'quickSale.email' })}
        changed={draft.user?.email !== undefined}
      >
        {editing ? (
          <input
            type="email"
            className="form-control form-control-sm"
            value={email}
            onChange={(event) => onUserChange('email', event.target.value)}
          />
        ) : (
          <span className="text-break">{email}</span>
        )}
      </FieldRow>
      <FieldRow
        label={intl.formatMessage({ id: 'quickSale.phone' })}
        changed={draft.user?.phone !== undefined}
      >
        {editing ? (
          <input
            type="tel"
            className="form-control form-control-sm"
            value={phone || ''}
            onChange={(event) => onUserChange('phone', event.target.value)}
          />
        ) : (
          phone || '-'
        )}
      </FieldRow>
      <FieldRow
        label={intl.formatMessage({ id: 'quickSale.deliveryAddress' })}
        changed={ADDRESS_FIELDS.some((field) => field in draftAddress)}
      >
        {editing && address ? (
          <div className="d-flex flex-column gap-2">
            {ADDRESS_FIELDS.map((field) => (
              <input
                key={field}
                className="form-control form-control-sm"
                placeholder={intl.formatMessage({
                  id: `quickSale.address_${field}`,
                })}
                value={draftAddress[field] ?? address[field] ?? ''}
                onChange={(event) => onAddressChange(field, event.target.value)}
              />
            ))}
          </div>
        ) : (
          formatAddress(address && { ...address, ...draftAddress })
        )}
      </FieldRow>
      <div className="pt-4">
        <Link to={`/user/${user.id}/profile`} className="link fw-bold">
          <i className="fas fa-external-link-alt me-2" />
          {intl.formatMessage({ id: 'quickSale.fullProfile' })}
        </Link>
      </div>
    </QuickSaleCard>
  );
};

export { CustomerCard };
